import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { PopOverProvider } from './PopOverContext';

export interface PopOverProps {
  children: ReactNode;
  /** 제어형: 현재 열림 상태 */
  open?: boolean;
  /** 비제어형: 초기 열림 상태 (기본: false) */
  defaultOpen?: boolean;
  /** 열림/닫힘 상태 변경 시 호출 */
  onOpenChange?: (open: boolean) => void;
  /** 위치 기준 엘리먼트. 지정 시 Content가 이 엘리먼트 기준으로 위치 계산 (triggerRef보다 우선). 외부 클릭 감지에서도 내부로 취급. */
  anchorEl?: HTMLElement | null;
}

export const PopOver = ({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  anchorEl = null,
}: PopOverProps) => {
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);

  const currentOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const triggerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const contentId = useId();

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const toggle = useCallback(() => {
    setOpen(!currentOpen);
  }, [setOpen, currentOpen]);
  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // ESC 키 + 외부 pointerdown (capture: 버블이 막혀도 document에서 먼저 처리)
  useEffect(() => {
    if (!currentOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };

    const handlePointerDownCapture = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      if (anchorEl?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      if (contentRef.current?.contains(target)) return;

      close();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDownCapture, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDownCapture, true);
    };
  }, [currentOpen, close, anchorEl]);

  const contextValue = useMemo(
    () => ({ open: currentOpen, contentId, toggle, close, triggerRef, contentRef, anchorEl }),
    [currentOpen, contentId, toggle, close, anchorEl],
  );

  return <PopOverProvider value={contextValue}>{children}</PopOverProvider>;
};
